import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../lib/romEngine";
import { BillingStatusBadge } from "../../components/BillingStatusBadge";
import {
    Kanban, Plus, X, CircleNotch, MapPin, CurrencyDollar, ArrowsClockwise, Link as LinkIcon,
    Funnel, ArrowRight, ListChecks, MagnifyingGlass, ListBullets,
} from "@phosphor-icons/react";

const CARDS_PER_COLUMN = 3;

const STAGE_LABELS = {
    rom: "ROM / Quote",
    review: "Adjustor / Insurer Review",
    go_no_go: "Go / No-Go",
    execution: "Execution (WIP)",
    invoice: "Invoice",
    pay: "Payment",
    ar: "AR / Closed",
};

const STAGE_ACCENT = {
    rom: "var(--rom-navy)", review: "#7c5cff", go_no_go: "#d97706",
    execution: "var(--rom-orange)", invoice: "#0891b2", pay: "var(--rom-green)", ar: "#64748b",
};

const HEALTH_DOT = { on_track: "var(--rom-green)", at_risk: "#d97706", off_track: "#dc2626" };
const HEALTH_LABEL = { on_track: "On track", at_risk: "At risk", off_track: "Off track" };

function pnlColor(pct) {
    if (pct == null) return "var(--rom-text-2)";
    if (pct < 0) return "#dc2626";
    if (pct < 15) return "#d97706";
    return "var(--rom-green)";
}
function burnColor(pct) {
    if (pct == null) return "var(--rom-navy)";
    if (pct > 100) return "#dc2626";
    if (pct > 85) return "#d97706";
    return "var(--rom-green)";
}

export default function ProjectBoard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = searchParams.get("filter"); // "at_risk" | "overdue_tasks" | null
    const [projects, setProjects] = useState([]);
    const [meta, setMeta] = useState({ stages: [], wip_substages: [] });
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dragId, setDragId] = useState(null);
    const [dragOver, setDragOver] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [q, setQ] = useState(() => searchParams.get("loss_type") || "");
    const [expanded, setExpanded] = useState(new Set());
    const [view, setView] = useState("board"); // "board" | "list" — list defaults to a stacked card on phones

    async function load() {
        setLoading(true);
        try {
            const [pRes, mRes, jRes, cRes] = await Promise.all([endpoints.projects(), endpoints.projectMeta(), endpoints.jobs(), endpoints.clients()]);
            setProjects(pRes.data || []);
            setMeta(mRes.data || { stages: [], wip_substages: [] });
            setJobs(jRes.data || []);
            setClients(cRes.data || []);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const clientNameById = useMemo(() => {
        const m = {};
        clients.forEach((c) => { m[c.id] = c.name; });
        return m;
    }, [clients]);

    function toggleExpand(stage) {
        setExpanded((s) => {
            const n = new Set(s);
            if (n.has(stage)) n.delete(stage); else n.add(stage);
            return n;
        });
    }

    const isActive = (p) => p.status !== "closed" && p.stage !== "ar";
    const isAtRisk = (p) => isActive(p) && ["at_risk", "off_track"].includes(p.health?.status);

    const todayStr = new Date().toISOString().slice(0, 10);
    const overdueTaskItems = useMemo(() => {
        const items = [];
        projects.forEach((p) => {
            if (!isActive(p)) return;
            (p.tasks || []).forEach((t) => {
                if ((t.status || "") === "done") return;
                if (t.due_date && String(t.due_date).slice(0, 10) < todayStr) {
                    items.push({ ...t, project_id: p.id, project_name: p.name });
                }
            });
        });
        return items;
    }, [projects, todayStr]);

    const displayProjects = useMemo(() => {
        let rows = filter === "at_risk" ? projects.filter(isAtRisk) : projects;
        const term = q.trim().toLowerCase();
        if (term) {
            rows = rows.filter((p) => {
                const clientName = clientNameById[p.client_id] || clientNameById[p.insurer_id] || "";
                return `${p.name || ""} ${p.loss_type || ""} ${p.address || ""} ${p.project_number || ""} ${clientName}`
                    .toLowerCase().includes(term);
            });
        }
        return rows;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, filter, q, clientNameById]);

    const byStage = useMemo(() => {
        const m = {};
        (meta.stages || []).forEach((s) => { m[s] = []; });
        displayProjects.forEach((p) => { (m[p.stage] = m[p.stage] || []).push(p); });
        return m;
    }, [displayProjects, meta.stages]);

    async function moveTo(projectId, stage) {
        const proj = projects.find((p) => p.id === projectId);
        if (!proj || proj.stage === stage) return;
        setProjects((ps) => ps.map((p) => p.id === projectId ? { ...p, stage } : p)); // optimistic
        try { await endpoints.updateProject(projectId, { stage }); }
        catch (e) { alert(formatApiError(e)); load(); }
    }

    async function setSubstage(projectId, wip_substage) {
        setProjects((ps) => ps.map((p) => p.id === projectId ? { ...p, wip_substage } : p));
        try { await endpoints.updateProject(projectId, { wip_substage }); } catch { load(); }
    }

    const totalValue = displayProjects.reduce((s, p) => s + (p.contract_value || p.rom_value || 0), 0);
    const clearFilter = () => setSearchParams({});

    return (
        <div className="px-6 py-8 space-y-5" data-testid="project-board-page">
            <header className="flex flex-wrap items-end justify-between gap-4 max-w-[1700px]">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Projects</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <Kanban size={28} weight="bold" className="text-[var(--rom-orange)]" /> Pipeline
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">{displayProjects.length} projects · {formatCurrency(totalValue)} in play. Drag cards across the workflow.</p>
                </div>
                <div className="flex gap-2">
                    <div className="inline-flex rounded-[6px] border border-[var(--rom-border-strong)] overflow-hidden" data-testid="project-view-toggle">
                        <button onClick={() => setView("board")} data-testid="project-view-toggle-board"
                            className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 transition-colors ${view === "board" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)] hover:bg-slate-50"}`}>
                            <Kanban size={13} weight="bold" /> Board
                        </button>
                        <button onClick={() => setView("list")} data-testid="project-view-toggle-list"
                            className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 border-l border-[var(--rom-border-strong)] transition-colors ${view === "list" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)] hover:bg-slate-50"}`}>
                            <ListBullets size={13} weight="bold" /> List
                        </button>
                    </div>
                    <button onClick={load} className="btn btn-ghost"><ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
                    <button onClick={() => setShowNew(true)} className="btn btn-primary" data-testid="new-project-btn"><Plus size={14} weight="bold" /> New project</button>
                </div>
            </header>

            <div className="relative max-w-sm" data-testid="project-search-wrap">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, loss type, client…"
                    className="input pl-9"
                    data-testid="project-search-input"
                />
                {q && (
                    <button onClick={() => setQ("")} data-testid="project-search-clear"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)] hover:text-[var(--rom-text)]">
                        <X size={13} weight="bold" />
                    </button>
                )}
            </div>

            {filter === "at_risk" && (
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-[6px] border border-[#fecaca] bg-red-50 text-sm" data-testid="filter-banner-at-risk">
                    <Funnel size={14} weight="bold" className="text-red-600" />
                    <span className="font-semibold text-red-700">Showing at-risk projects</span>
                    <span className="text-[var(--rom-text-3)]">({displayProjects.length})</span>
                    <button onClick={clearFilter} className="ml-auto text-xs font-semibold text-red-700 hover:underline flex items-center gap-1" data-testid="clear-filter-btn"><X size={12} weight="bold" /> Clear filter</button>
                </div>
            )}

            {filter === "overdue_tasks" ? (
                <div className="space-y-3" data-testid="overdue-tasks-filter-view">
                    <div className="flex items-center gap-2 px-4 py-2.5 rounded-[6px] border border-[#fecaca] bg-red-50 text-sm">
                        <Funnel size={14} weight="bold" className="text-red-600" />
                        <span className="font-semibold text-red-700">Showing overdue tasks</span>
                        <span className="text-[var(--rom-text-3)]">({overdueTaskItems.length})</span>
                        <button onClick={clearFilter} className="ml-auto text-xs font-semibold text-red-700 hover:underline flex items-center gap-1" data-testid="clear-filter-btn"><X size={12} weight="bold" /> Clear filter</button>
                    </div>
                    {overdueTaskItems.length === 0 ? (
                        <div className="rom-card"><div className="rom-card-body text-center py-10 text-[var(--rom-text-3)]">No overdue tasks. Nicely run.</div></div>
                    ) : (
                        <div className="rom-card overflow-hidden">
                            <div className="rom-card-head"><h3 className="flex items-center gap-2"><ListChecks size={16} weight="bold" /> Overdue tasks</h3></div>
                            <div className="divide-y divide-[var(--rom-border)]">
                                {overdueTaskItems.map((t) => (
                                    <button key={t.id} onClick={() => navigate(`/projects/${t.project_id}`)} data-testid={`overdue-task-${t.id}`}
                                        className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50 transition-colors">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold truncate">{t.title || "Untitled task"}</div>
                                            <div className="text-xs text-[var(--rom-text-2)] truncate">{t.project_name} · due {t.due_date}</div>
                                        </div>
                                        <ArrowRight size={14} weight="bold" className="text-[var(--rom-text-3)] shrink-0" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : view === "list" ? (
                <div className="rom-card overflow-hidden" data-testid="project-list-view">
                    <table className="rom-table rom-table--responsive" data-testid="project-list-table">
                        <thead>
                            <tr>
                                <th>Project</th>
                                <th>Stage</th>
                                <th>Health</th>
                                <th className="numeric">Complete</th>
                                <th className="numeric">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayProjects.length === 0 && (
                                <tr><td colSpan={5} className="text-center text-[var(--rom-text-3)] py-10">No projects match.</td></tr>
                            )}
                            {displayProjects.map((p) => (
                                <tr key={p.id} className="cursor-pointer" onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-list-row-${p.id}`}>
                                    <td className="card-title">
                                        <div className="font-semibold">{p.name}</div>
                                        <div className="text-[11px] text-[var(--rom-text-3)] font-mono">{p.project_number}</div>
                                        {p.address && <div className="text-[11px] text-[var(--rom-text-2)] flex items-center gap-1 mt-0.5"><MapPin size={11} /> <span className="truncate">{p.address}</span></div>}
                                    </td>
                                    <td data-label="Stage"><span className="chip chip-navy text-[10px]" style={{ color: STAGE_ACCENT[p.stage] }}>{STAGE_LABELS[p.stage] || p.stage}</span></td>
                                    <td data-label="Health">
                                        {p.health?.status ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide" style={{ color: HEALTH_DOT[p.health.status] }}>
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: HEALTH_DOT[p.health.status] }} />{HEALTH_LABEL[p.health.status]}
                                            </span>
                                        ) : "—"}
                                    </td>
                                    <td className="numeric" data-label="Complete">{p.progress_pct != null ? `${Math.round(p.progress_pct)}%` : "—"}</td>
                                    <td className="numeric font-semibold" data-label="Value">{formatCurrency(p.contract_value || p.rom_value)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
            <div className="overflow-x-auto pb-4">
                <div className="flex gap-4 min-w-fit">
                    {(meta.stages || []).map((stage) => {
                        const cards = byStage[stage] || [];
                        const colTotal = cards.reduce((s, p) => s + (p.contract_value || p.rom_value || 0), 0);
                        const isExpanded = expanded.has(stage);
                        const visible = isExpanded ? cards : cards.slice(0, CARDS_PER_COLUMN);
                        const hiddenCount = cards.length - visible.length;
                        return (
                            <div key={stage}
                                data-testid={`column-${stage}`}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
                                onDragLeave={() => setDragOver((d) => d === stage ? null : d)}
                                onDrop={() => { if (dragId) moveTo(dragId, stage); setDragId(null); setDragOver(null); }}
                                className={`w-[280px] shrink-0 rounded-[8px] border transition-colors ${dragOver === stage ? "border-[var(--rom-orange)] bg-[var(--rom-orange-soft)]/30" : "border-[var(--rom-border)] bg-slate-50/60"}`}
                            >
                                <div className="px-3 py-2.5 border-b border-[var(--rom-border)] flex items-center justify-between sticky top-0 bg-white/90 backdrop-blur rounded-t-[8px]" style={{ borderTop: `3px solid ${STAGE_ACCENT[stage]}` }}>
                                    <div className="text-xs font-bold uppercase tracking-wider" style={{ color: STAGE_ACCENT[stage] }}>{STAGE_LABELS[stage] || stage}</div>
                                    <span className="chip chip-gray text-[10px]">{cards.length}</span>
                                </div>
                                <div className="p-2 space-y-2 min-h-[120px]">
                                    {colTotal > 0 && <div className="text-[10px] text-[var(--rom-text-3)] px-1 font-mono">{formatCurrency(colTotal)}</div>}
                                    {visible.map((p) => (
                                        <div key={p.id}
                                            data-testid={`project-card-${p.id}`}
                                            draggable
                                            onDragStart={() => setDragId(p.id)}
                                            onDragEnd={() => { setDragId(null); setDragOver(null); }}
                                            onClick={() => navigate(`/projects/${p.id}`)}
                                            className="bg-white border border-[var(--rom-border)] rounded-[6px] p-3 cursor-pointer hover:shadow-md hover:border-[var(--rom-navy)] transition-all"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="text-[10px] font-mono text-[var(--rom-text-3)]">{p.project_number}</div>
                                                <div className="flex items-center gap-1.5">
                                                    {p.health?.status && (
                                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide" style={{ color: HEALTH_DOT[p.health.status] }} title={(p.health.reasons || []).join(" · ")} data-testid={`card-health-${p.id}`}>
                                                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: HEALTH_DOT[p.health.status] }} />{HEALTH_LABEL[p.health.status]}
                                                        </span>
                                                    )}
                                                    {p.rom_job_id ? <LinkIcon size={12} className="text-[var(--rom-orange)]" weight="bold" title="Linked to a ROM" /> : null}
                                                </div>
                                            </div>
                                            <div className="font-semibold text-sm mt-1 leading-snug line-clamp-2">{p.name}</div>
                                            {p.address && <div className="text-[11px] text-[var(--rom-text-2)] mt-1 flex items-center gap-1"><MapPin size={11} /> <span className="truncate">{p.address}</span></div>}
                                            {p.progress_pct != null && (
                                                <div className="mt-2" data-testid={`card-progress-${p.id}`}>
                                                    <div className="flex items-center justify-between text-[10px] mb-1"><span className="text-[var(--rom-text-3)]">Complete</span><span className="font-semibold">{Math.round(p.progress_pct)}%</span></div>
                                                    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-[var(--rom-navy)]" style={{ width: `${Math.min(100, p.progress_pct)}%` }} /></div>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between mt-2">
                                                <span className="chip chip-navy capitalize text-[10px]">{p.loss_type || "—"}</span>
                                                <span className="font-mono font-bold text-sm">{formatCurrency(p.contract_value || p.rom_value)}</span>
                                            </div>
                                            <div className="mt-1.5"><BillingStatusBadge status={p.billing_status} /></div>
                                            {p.pnl && p.pnl.revenue > 0 && (
                                                <div className="mt-2" data-testid={`pnl-bar-${p.id}`}>
                                                    <div className="flex items-center justify-between text-[10px] mb-1">
                                                        <span className="text-[var(--rom-text-3)]">Burn {p.pnl.burn_pct ?? 0}%</span>
                                                        <span className="font-semibold" style={{ color: pnlColor(p.pnl.margin_pct) }}>
                                                            {p.pnl.margin >= 0 ? "+" : ""}{formatCurrency(p.pnl.margin)} · {p.pnl.margin_pct ?? 0}%
                                                        </span>
                                                    </div>
                                                    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                                        <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, p.pnl.burn_pct || 0)}%`, background: burnColor(p.pnl.burn_pct) }} />
                                                    </div>
                                                </div>
                                            )}
                                            {stage === "execution" && (
                                                <select
                                                    data-testid={`wip-substage-${p.id}`}
                                                    value={p.wip_substage || ""}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => { e.stopPropagation(); setSubstage(p.id, e.target.value); }}
                                                    className="mt-2 w-full h-7 px-2 rounded-[4px] border border-[var(--rom-border)] text-[11px] bg-white"
                                                >
                                                    <option value="">WIP stage…</option>
                                                    {(meta.wip_substages || []).map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    ))}
                                    {cards.length === 0 && <div className="text-[11px] text-[var(--rom-text-3)] text-center py-4">Drop here</div>}
                                    {cards.length > CARDS_PER_COLUMN && (
                                        <button onClick={() => toggleExpand(stage)} data-testid={`project-stage-toggle-${stage}`}
                                            className="w-full text-center text-[11px] font-semibold text-[var(--rom-navy)] hover:underline py-1.5">
                                            {isExpanded ? "Show less" : `Show all (${hiddenCount} more)`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            )}

            {showNew && <NewProjectModal jobs={jobs} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
        </div>
    );
}

function NewProjectModal({ jobs, onClose, onCreated }) {
    const navigate = useNavigate();
    const [mode, setMode] = useState("blank"); // blank | rom
    const [form, setForm] = useState({ name: "", loss_type: "water", address: "", contract_value: "" });
    const [jobId, setJobId] = useState("");
    const [busy, setBusy] = useState(false);
    const unconverted = jobs;

    async function create() {
        setBusy(true);
        try {
            let res;
            if (mode === "rom") {
                if (!jobId) { alert("Pick a ROM"); setBusy(false); return; }
                res = await endpoints.projectFromRom(jobId);
            } else {
                if (!form.name.trim()) { alert("Name required"); setBusy(false); return; }
                res = await endpoints.createProject({
                    name: form.name.trim(), loss_type: form.loss_type, address: form.address,
                    contract_value: Number(form.contract_value) || 0,
                });
            }
            onCreated();
            if (res?.data?.id) navigate(`/projects/${res.data.id}`);
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }

    const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none";
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
            <div className="bg-white rounded-lg w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="new-project-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rom-border)]">
                    <h3 className="font-display font-bold text-lg">New project</h3>
                    <button onClick={onClose} className="p-1 text-[var(--rom-text-2)]"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="flex gap-0 rounded-[6px] overflow-hidden border border-[var(--rom-border)] w-fit">
                        <button data-testid="np-mode-blank" onClick={() => setMode("blank")} className={`px-4 py-2 text-sm font-semibold ${mode === "blank" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>Standalone</button>
                        <button data-testid="np-mode-rom" onClick={() => setMode("rom")} className={`px-4 py-2 text-sm font-semibold border-l border-[var(--rom-border)] ${mode === "rom" ? "bg-[var(--rom-orange)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>Convert a ROM</button>
                    </div>
                    {mode === "rom" ? (
                        <div>
                            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Pick a ROM / job</label>
                            <select data-testid="np-rom-select" value={jobId} onChange={(e) => setJobId(e.target.value)} className={inputCls}>
                                <option value="">Select…</option>
                                {unconverted.map((j) => <option key={j.id} value={j.id}>{j.ref} · {j.loss_type} · {formatCurrency(j.result?.grand_total)}</option>)}
                            </select>
                            <p className="text-xs text-[var(--rom-text-3)] mt-1">Pulls client, address, loss type and ROM value across. Lands in the Review column.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2"><label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Project name *</label><input data-testid="np-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} /></div>
                            <div><label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Loss type</label>
                                <select data-testid="np-loss" value={form.loss_type} onChange={(e) => setForm((f) => ({ ...f, loss_type: e.target.value }))} className={inputCls}>
                                    {["water", "fire", "mould", "storm"].map((l) => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </div>
                            <div><label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Contract value ($)</label><input data-testid="np-value" type="number" value={form.contract_value} onChange={(e) => setForm((f) => ({ ...f, contract_value: e.target.value }))} className={inputCls} /></div>
                            <div className="col-span-2"><label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Address</label><input data-testid="np-address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} className={inputCls} /></div>
                        </div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-[var(--rom-border)] flex justify-end gap-2">
                    <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="btn btn-primary" onClick={create} disabled={busy} data-testid="np-create">{busy ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} Create</button>
                </div>
            </div>
        </div>
    );
}
